import React, { useCallback, useMemo, useState } from 'react';
import { Target } from 'lucide-react';
import { ObjectiveCardFY2627 } from '../features/tracking_fy2627';
import PersonOKRSearch from '../components/PersonOKRSearch';
import { buildKeyResultsWithOwnerChange, buildKeyResultsWithQuarterEdit } from '../lib/personOkrIndex';

/** Why a reassign was refused, in the admin's words rather than the helper's. */
const REASSIGN_MESSAGES = {
  unchanged: 'That person already owns this IKR.',
  'duplicate-owner': 'That person already owns an IKR under this Team KR. Move or merge it there instead.',
};

export default function TrackingViewFY2627({ okrs, handleUpdateOkr, handleDeleteOkr, users, viewer, onOwnerAssigned, nextKrCode, searchOkrs, onReassignIkrOwner }) {
  const [personFilterId, setPersonFilterId] = useState(null);

  const searchScope = useMemo(() => searchOkrs || okrs, [searchOkrs, okrs]);

  const handleSelectionChange = useCallback((person) => {
    setPersonFilterId(person ? person.id : null);
  }, []);

  /** Persist a single quarter Target / Actual edited from the person panel. */
  const handleQuarterChange = useCallback((path, quarterIndex, field, value) => {
    const objective = searchScope.find(o => o.id === path.objectiveId);
    const keyResults = buildKeyResultsWithQuarterEdit(objective, path, quarterIndex, field, value, 'FY2627');
    if (keyResults) handleUpdateOkr(path.objectiveId, { keyResults });
  }, [searchScope, handleUpdateOkr]);

  /**
     * Move one IKR to a different owner from the person panel. Admin only — the
     * write touches two user documents, which firestore.rules allows only for an
     * admin. Deliberately not routed through `onOwnerAssigned`: that one only adds
     * a grant, leaving the previous owner's behind, which is the bug this fixes.
     */
  const handleReassign = useCallback(async (path, newOwnerId) => {
    const objective = searchScope.find(o => o.id === path.objectiveId);
    const result = buildKeyResultsWithOwnerChange(objective, path, newOwnerId, 'FY2627');
    if (!result) return { ok: false, message: 'This IKR is no longer available — refresh and try again.' };
    if (!result.ok) return { ok: false, message: REASSIGN_MESSAGES[result.reason] || 'Reassign refused.' };
    return onReassignIkrOwner({
      variant: 'FY2627',
      okrId: path.objectiveId,
      keyResults: result.keyResults,
      previousOwnerId: result.previousOwnerId,
      newOwnerId: newOwnerId || null,
      krCode: result.krCode,
    });
  }, [searchScope, onReassignIkrOwner]);

  const personSearch = (
    <PersonOKRSearch
      okrs={searchScope}
      users={users}
      variant="FY2627"
      viewerUid={viewer.uid || viewer.id}
      canEditTarget={viewer.role === 'Admin'}
      canEditAllActuals={viewer.role === 'Admin'}
      canReassign={viewer.role === 'Admin'}
      onQuarterChange={handleQuarterChange}
      onReassign={handleReassign}
      onSelectionChange={handleSelectionChange}
    />
  );

  // While a person is selected, their tree replaces the full objective list.
  if (personFilterId) return <div>{personSearch}</div>;

  if (okrs.length === 0) {
    return (
      <div>
        {personSearch}
        <div className="text-center py-20 bg-white rounded-xl shadow-md">
          <Target size={48} className="mx-auto text-gray-300" />
          <h3 className="mt-4 text-xl font-semibold text-gray-700">No Objectives Found</h3>
          <p className="text-gray-500 mt-2">Select an objective from the sidebar to view its Key Results.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {personSearch}

      {/* ── Objective Cards ───────────────────────────────────── */}
      {okrs.map(okr => (
        <ObjectiveCardFY2627
          key={okr.id}
          objective={okr}
          onUpdate={handleUpdateOkr}
          onDelete={handleDeleteOkr}
          users={users}
          viewer={viewer}
          onOwnerAssigned={onOwnerAssigned}
          nextKrCode={nextKrCode}
        />
      ))}
    </div>
  );
}
